import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getStatusColor, getDisplayColor } from '@/lib/statusColors';
import { useTheme } from '@/context/ThemeContext';
import VanLightPng from '@/assets/perfiles-icono.png';
import VanDarkPng from '@/assets/perfiles-icono1.png';
import VanLightOnPng from '@/assets/perfiles-iconoON.png';
import VanDarkOnPng from '@/assets/perfiles-icono1ON.png';
import MaskPng from '@/assets/perfiles-icono-mask.png';

/**
 * ProfileSelector: Modal reutilizable para seleccionar perfil entre 5 o más
 * - Muestra todos los perfiles en cuadrícula scrollable
 * - Indica visualmente el perfil activo
 * - Cierra tras seleccionar
 */
export function ProfileSelector({
  open,
  onOpenChange,
  profiles,
  selectedUser,
  routeStatus,
  colorCodeEnabled,
  onProfileSelect,
  isProfileLocked,
  permissions,
  userRole,
  allowCrossProfileClientsConsultation,
}) {
  const { theme } = useTheme();
  const VanPng = theme === 'dark' ? VanDarkPng : VanLightPng;

  const canConsultInformational =
    userRole === 'worker' && allowCrossProfileClientsConsultation === true;

  // Delega toda la decisión de bloqueo/PIN/trusted device en el padre
  // (AppHeader.attemptProfileSwitch), reutilizando el mismo flujo que la
  // rama de 1-4 perfiles. Este componente NO decide si el perfil está
  // bloqueado ni abre PinDialog: solo aplica el filtro de perfiles
  // informativos no consultables y entrega el objeto profile completo.
  const handleProfileClick = async (profile, isInformational) => {
    if (isInformational && !canConsultInformational) {
      return;
    }
    onOpenChange(false);
    await onProfileSelect(profile);
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[70vh] overflow-y-auto"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Seleccionar Perfil</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4 py-4">
       {profiles.map((profile, index) => {
             const profileId = String(profile.id ?? profile.name);
             const isSelected = profileId === selectedUser;
             const isLocked = isProfileLocked(profileId);
             const isInformational = profile.isInformational === true;

             // Prioridad: routeStatus > profile.statusKey > null
             const rawStatus = routeStatus?.[profileId] ?? profile.statusKey;
             // Solo mostrar color del estado ajeno si tiene canUseSharedRouteStates
             const isOtherProfile = profileId !== selectedUser;
             const canSeeSharedStatus = !isOtherProfile || permissions?.canUseSharedRouteStates;
             const profileStatus =
               colorCodeEnabled && canSeeSharedStatus && typeof rawStatus === 'string' && rawStatus.trim().length > 0
                 ? rawStatus
                 : null;

             const statusColor = profileStatus ? getStatusColor(profileStatus) : null;
             const isInformationalBlocked = isInformational && !canConsultInformational;

             return (
               <button
                 key={index}
                 onClick={() => handleProfileClick(profile, isInformational)}

                 disabled={isInformationalBlocked}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1 p-2 rounded-lg transition-colors relative',
                    isSelected && !isInformational && profiles.length > 1
                      ? 'bg-primary/20 border-2 border-primary'
                      : 'bg-muted hover:bg-muted/80 border-2 border-transparent',
                    isInformationalBlocked ? 'opacity-50 cursor-not-allowed' : ''
                  )}
                 title={isInformationalBlocked ? `${profile.name} (Solo información)` : profile.name}
               >
                <div style={{ position: 'relative', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {profileStatus && (
                    <div
                      className="absolute pointer-events-none"
                      aria-hidden="true"
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: getDisplayColor(profileStatus, theme),
                        WebkitMaskImage: `url(${MaskPng})`,
                        maskImage: `url(${MaskPng})`,
                        WebkitMaskSize: 'contain',
                        maskSize: 'contain',
                        WebkitMaskPosition: 'center',
                        maskPosition: 'center',
                        WebkitMaskRepeat: 'no-repeat',
                        maskRepeat: 'no-repeat'
                      }}
                    />
                  )}
                  <img
                    src={
                      isSelected
                        ? (theme === 'dark' ? VanDarkOnPng : VanLightOnPng)
                        : VanPng
                    }
                    alt={profile.name}
                    className="object-contain select-none"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                </div>
                <span className="text-xs font-medium text-center truncate max-w-[70px]">
                  {profile.name.length > 8
                    ? profile.name.substring(0, 7).charAt(0).toUpperCase() + profile.name.substring(0, 7).slice(1).toLowerCase()
                    : profile.name.charAt(0).toUpperCase() + profile.name.slice(1).toLowerCase()}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
